// The scratchpad (Basemap Home v2, artemisapi docs/client-guide-scratchpad.md): with Context.Scratchpad
// on, Artemis drafts waypoints, routes and shapes on the conversation instead of saving them; the user
// ticks drafts and sends them to the map. ScratchItem:
//   { Id, Kind: 'waypoint'|'route'|'shape'|'chart'|'note', Title, Subtitle, Geometry (GeoJSON),
//     MarkerTypeId, Color, Notes, Data, Status: 'draft'|'sent', MessageId, Sent, CreatedAt, Position }

export const SCRATCH_KINDS = ['waypoint', 'route', 'shape', 'chart', 'note']
// Charts and notes stay on the scratchpad: Send refuses them (not_sendable).
export const isSendable = item => !!item && ['waypoint', 'route', 'shape'].includes(item.Kind)
// An unknown kind reads as a note.
export const kindOf = item => (item && SCRATCH_KINDS.includes(item.Kind) ? item.Kind : 'note')

// The items a step event added: { Kind: 'scratch_item', Item } or { Kind: 'scratch_items', Items }.
export const scratchFromStep = result => {
  if (!result || typeof result !== 'object') return []
  if (result.Kind === 'scratch_item' && result.Item && result.Item.Id) return [result.Item]
  if (result.Kind === 'scratch_items' && Array.isArray(result.Items)) return result.Items.filter(i => i && i.Id)
  return []
}

const byPosition = (a, b) => (a.Position ?? 0) - (b.Position ?? 0)

// Adds or replaces items by Id, keeping Position order.
export const mergeItems = (items, incoming) => {
  const next = new Map((items || []).map(i => [i.Id, i]))
  ;(incoming || []).forEach(i => { if (i && i.Id) next.set(i.Id, i) })
  return [...next.values()].sort(byPosition)
}

// After Send: each sent item becomes sent with its object; failures are returned by item id.
export const applySend = (items, response) => {
  const at = new Date().toISOString()
  const sent = new Map(((response && response.Sent) || []).map(s => [s.ItemId, s]))
  const updated = (items || []).map(i => {
    const s = sent.get(i.Id)
    if (!s || i.Status === 'sent') return i
    return { ...i, Status: 'sent', Sent: { ObjectKind: s.ObjectKind, ObjectId: s.ObjectId, FolderId: response.FolderId || null, At: at, OpenInBasemap: s.OpenInBasemap || null } }
  })
  const failed = {}
  ;((response && response.Failed) || []).forEach(f => { failed[f.ItemId] = f })
  return { items: updated, failed }
}

const FAILURE_TEXT = {
  needs_input: 'Pick a waypoint type first',
  not_sendable: 'Stays on the scratchpad',
  object_limit_reached: 'Your map is full',
  upstream_unavailable: 'Couldn’t reach BaseMap. Try again.'
}
export const failureText = failure => (failure && (FAILURE_TEXT[failure.Error] || failure.Message)) || 'Couldn’t send this one'

// The toast after Send: "3 items added to your map", plus "1 couldn't be sent" when some failed.
export const sendSummary = response => {
  const sent = ((response && response.Sent) || []).length
  const failed = ((response && response.Failed) || []).length
  return [
    sent ? `${sent} ${sent === 1 ? 'item' : 'items'} added to your map` : '',
    failed ? `${failed} couldn’t be sent` : ''
  ].filter(Boolean).join(' · ') || 'Nothing to send'
}

// Every [lng, lat] in an item's geometry, for fitting the scratchpad map.
export const itemPositions = item => {
  const g = item && item.Geometry
  if (!g || !g.coordinates) return []
  const out = []
  const walk = c => {
    if (!Array.isArray(c)) return
    if (typeof c[0] === 'number' && typeof c[1] === 'number') out.push([c[0], c[1]])
    else c.forEach(walk)
  }
  walk(g.coordinates)
  return out
}

// A waypoint draft can't be sent without a marker type (Send answers needs_input).
export const needsType = item => !!item && item.Kind === 'waypoint' && item.Status !== 'sent' && !item.MarkerTypeId

// The app's marker types ({ id, name } from MarkerType/Get) as picker options, by name.
export const markerTypeOptions = types => (Array.isArray(types) ? types : [])
  .filter(t => t && t.id != null && t.name)
  .map(t => ({ id: t.id, name: String(t.name) }))
  .sort((a, b) => a.name.localeCompare(b.name))
